import React, { FC, useMemo, useState } from "react";
import "./wsInvActCard.scss";
import { CardHeader } from "@shared/ui/card-header";
import { useActionSheet, useIsOverlayOpenByKeys, useWindows } from "@app/overlay";
import { useRequiredNumberParam } from "@src/hooks/useRequiredNumberParam";
import {
    BusinessBoStateValue,
    BusinessDivisionValue,
    BusinessDateValue,
    formatBoStateText,
    formatDivisionText,
} from "@src/components/business-data-display";
import { API_USER_OPERATION_LIST, useBoAvOperations, useBoRights } from "@src/api/bo";
import type { ActionSheetSection } from "@shared/ui/overlay/action-sheet";
import { DetailList } from "@shared/ui/detail-row";
import { CardLoader } from "@src/components/card-loader";
import BarcodeScanner from "@src/components/service/BarcodeScanner/barcodeScanner";
import notification from "@app/services/notification";
import { WsInvContentsList } from "./components/WsInvContentsList/WsInvContentsList";
import { useWsInvActCardQuery } from "./hooks/useWsInvActCardQuery";
import { useWsInvActContentsQuery } from "./hooks/useWsInvActContentsQuery";
import { useWsInvActCardMutations } from "./useWsInvActCardMutations";
import { wsInvActCardWindows } from "./wsInvActCard.windows";
import { resolveWsInvActContentSearch } from "./helpers/resolveWsInvActContentSearch";
import type { OverlayHandle } from "@app/overlay";
import type { WsInvActSearchResolution } from "./components/WsInvActCardSearch/WsInvActCardSearchForm";
import { useBoOperationConfirm } from "@src/businessModules/WorkShop/shared/bo/useBoOperationConfirm";
import { useBoOperationActionItems } from "@src/businessModules/WorkShop/shared/bo/useBoOperationActionItems";

const WS_INV_ACT_SCAN_HINT = {
    title: "Сканируйте обозначение ТМЦ",
    description: "Найдём строку состава акта инвентаризации.",
};

const WS_INV_ACT_CARD_SCAN_PAUSE_WINDOW_KEYS = [
    wsInvActCardWindows.search.key,
    wsInvActCardWindows.indefiniteResult.key,
    wsInvActCardWindows.content.key,
] as const;

const WsInvActCard: FC = () => {
    const actCode = useRequiredNumberParam("code", "код АИ");
    const windows = useWindows();
    const actionSheet = useActionSheet();
    const isScannerPausedByCardWindow = useIsOverlayOpenByKeys(WS_INV_ACT_CARD_SCAN_PAUSE_WINDOW_KEYS);

    const cardQuery = useWsInvActCardQuery(actCode);
    const contentsQuery = useWsInvActContentsQuery(actCode);
    const invAct = cardQuery.data;
    const avOperationsQuery = useBoAvOperations(invAct?.Code, invAct != null);

    const { hasRight } = useBoRights({
        boCode: invAct?.Code,
        rightCodes: [API_USER_OPERATION_LIST["Редактирование объекта"]],
        enabled: invAct != null,
    });
    const readonly = !hasRight(API_USER_OPERATION_LIST["Редактирование объекта"]);

    const [showBoInfo, setShowBoInfo] = useState(true);

    const mutations = useWsInvActCardMutations({
        onContentFactQtyPatch: (contentCode, factQty) => {
            contentsQuery.patchContentFactQty(contentCode, factQty);
        },
        onBoOperationSuccess: () => {
            void cardQuery.refetch();
            void contentsQuery.refetch();
            void avOperationsQuery.refetch();
        },
    });

    const { confirmAndPerform } = useBoOperationConfirm({
        perform: mutations.performBoOperationMutation.mutateAsync,
    });

    const operationItems = useBoOperationActionItems({
        operations: avOperationsQuery.data,
        context: invAct,
        confirmAndPerform,
        buildParams: (operation, card) => {
            const status = formatBoStateText(card.Bo?.State, "short");

            return {
                operation,
                boCode: card.Code,
                documentLabel: `АИ УП №${card.CommandNum}`,
                fields: [
                    { label: "Подразделение", value: formatDivisionText(card.Division, card.DivisionSection) },
                    { label: "Статус", value: status === "" ? "—" : status },
                ],
            };
        },
    });

    const actionSheetSections = useMemo<ActionSheetSection[]>(() => {
        const sections: ActionSheetSection[] = [
            {
                id: "view",
                items: [
                    {
                        type: "toggle",
                        id: "show-bo-info",
                        label: "Информация о БО",
                        description: "Отображать доп. информацию о карточке",
                        checked: showBoInfo,
                        onCheckedChange: setShowBoInfo,
                    },
                ],
            },
        ];

        if (operationItems.length > 0) {
            sections.push({
                id: "bo",
                items: [
                    {
                        type: "drill-in",
                        id: "bo-operations",
                        label: "Выполнить операцию",
                        description: "Выбрать доступную BO-операцию",
                        sections: [{ id: "bo-operations", items: operationItems }],
                    },
                ],
            });
        }

        return sections;
    }, [operationItems, showBoInfo]);

    const openContentWindow = (contentCode: number): void => {
        if (invAct == null) {
            return;
        }

        wsInvActCardWindows.content.open(windows, {
            contentCode,
            actNum: invAct.CommandNum,
            readonly,
            setFactQtyMutation: mutations.setFactQtyMutation,
        });
    };

    const handleSearchResolution = async (
        resolution: WsInvActSearchResolution,
        searchOverlay?: OverlayHandle,
    ): Promise<void> => {
        if (resolution.type === "single") {
            openContentWindow(resolution.contentCode);
            searchOverlay?.close();
            return;
        }

        searchOverlay?.close();
        const indefiniteOverlay = wsInvActCardWindows.indefiniteResult.open(windows, {
            stockObjSign: resolution.stockObjSign,
            choices: resolution.choices,
            onSelect: async (contentCode) => {
                openContentWindow(contentCode);
                indefiniteOverlay.close();
            },
        });
    };

    const handleSearchResult = async (stockObjSign: string): Promise<void> => {
        const result = await resolveWsInvActContentSearch(actCode, stockObjSign);

        if (result.type === "notFound") {
            notification.warning(
                `Не найдено состава акта инвентаризации с "Обозначением ТМЦ" со значением "${stockObjSign}"`,
            );
            return;
        }

        await handleSearchResolution(
            result.type === "single"
                ? { type: "single", contentCode: result.contentCode, stockObjSign }
                : { type: "ambiguous", stockObjSign: result.stockObjSign, choices: result.choices },
        );
    };

    if (cardQuery.isFetching || invAct == null) {
        return <CardLoader header list />;
    }

    return (
        <div className="ws_inv_act_page">
            <CardHeader
                text={`Акт инвентаризации УП №${invAct.CommandNum}`}
                buttons={[
                    {
                        type: "icon",
                        text: "Действия",
                        srcIcon: "/images/common/more_vert.svg",
                        onClick: () => actionSheet.open({ title: "Действия", sections: actionSheetSections }),
                    },
                ]}
            />
            {showBoInfo && (
                <DetailList className="ws_inv_act_inform">
                    <DetailList.Row
                        label={"Подразделение"}
                        value={<BusinessDivisionValue division={invAct.Division} section={invAct.DivisionSection} />}
                    />
                    <DetailList.Row label={"Дата"} value={<BusinessDateValue value={invAct.CommandDate} />} />
                    <DetailList.Row
                        label={"Статус"}
                        value={<BusinessBoStateValue state={invAct.Bo?.State} labelSource="short" />}
                    />
                </DetailList>
            )}
            <WsInvContentsList
                contentsQuery={contentsQuery}
                onOpenContent={openContentWindow}
                onOpenSearch={() => {
                    const searchOverlay = wsInvActCardWindows.search.open(windows, {
                        actCode,
                        onResolved: (resolution) => handleSearchResolution(resolution, searchOverlay),
                    });
                }}
            />
            <BarcodeScanner
                enabled={!isScannerPausedByCardWindow}
                hint={WS_INV_ACT_SCAN_HINT}
                captureInEditable={false}
                errorSource="workshop.wsInvAct.card.scanSearch"
                onScan={handleSearchResult}
            />
        </div>
    );
};

export default WsInvActCard;
